import React, { useEffect, useId, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../redux/store";
import BackButton from "../../../../components/BackButton";
import Header from "../../../../components/Header";
import {
  getManagerApplicationsApplyForTeam,
  setSelectedCourseApplication,
  updateManagerApplicationStatus,
} from "../../../../redux/slices/manager.applications.slice";
import SpinnerLoader from "../../../../components/SpinnerLoader";
import { FaEllipsisH } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FaPen } from "react-icons/fa6";
import TabNavbar from "../../../../components/Tabbar";
import { MY_TEAM_TABS } from "../../../../utils/TabbarData";
import SearchWithFilter from "../../../../components/SearchWithFilter";
import { downloadDescriptionFile } from "../../../../redux/slices/learner.applications.slice";
import { showToast } from "../../../../utils/showToast";
import { extractCareerLevelNumber } from "../../../../utils/helper";
const STATUS_MAP: any = {
  ELIGIBLE: {
    label: "Eligible",
    color: "bg-green-500 text-white",
  },
  NOT_ELIGIBLE: {
    label: "Not Eligible",
    color: "bg-gray-500 text-white",
  },
  DRAFTED: {
    label: "DRAFTED",
    color: "bg-gray-500 text-white",
  },
  APPLIED: {
    label: "APPLIED",
    color: "bg-orange-500 text-white",
  },
  APPROVED_BY_MANAGER: {
    label: "MANAGER APPROVED",
    color: "bg-green-500 text-white",
  },
  CONFIRMED_BY_LEARNER: {
    label: "CONFIRMED BY LEARNER",
    color: "bg-green-500 text-white",
  },
  IN_PROGRESS: {
    label: "Session in Progress",
    color: "bg-purple-500 text-white",
  },
  WAITING: {
    label: "WAITING LIST",
    color: "bg-blue-500 text-white",
  },
  REJECTED_BY_MANAGER: {
    label: "MANAGER DECLINED",
    color: "bg-red-500 text-white",
  },
  REJECTED_BY_LEARNER: {
    label: "USER DECLINED",
    color: "bg-red-500 text-white",
  },
  APPLICATION_REJECTED_BY_LEARNER: {
    label: "USER DECLINED",
    color: "bg-red-500 text-white",
  },
  REJECTED_BY_TOA: {
    label: "DECLINED BY SYSTEM",
    color: "bg-red-500 text-white",
  },
  REJECTED_BY_HR: {
    label: "HR DECLINED",
    color: "bg-red-500 text-white",
  },
  CONFIRMED_BY_HR: {
    label: "CONFIRMED BY HR",
    color: "bg-green-500 text-white",
  },
  CONFIRMED_BY_MANAGER: {
    label: "CONFIRMED BY MANAGER",
    color: "bg-green-500 text-white",
  },
  HR_APPROVAL_PENDING: {
    label: "HR APPROVAL PENDING",
    color: "bg-orange-500 text-white",
  },
  APPROVED_BY_HR: {
    label: "HR APPROVED",
    color: "bg-green-500 text-white",
  },
};

function ManagerApplyForTeamApplications() {
  const { selectedCourseApplication, updateLoading, managerCourses, loading } =
    useSelector((state: RootState) => state.managerApplications) as any;

  const [confirmationMenu, setConfirmationMenu] = useState<{
    action: string;
    item: any;
    isAllow?: boolean;
  } | null>(null);

  const {
    course_id,
    course_name,
    minimum_job_level,
    training_languages_oc,
    category_type,
  } = selectedCourseApplication as any;

  const [dropdownEnabled, setDropdownEnabled] = useState(false); // Dropdown enable state
  const menuRef = useRef<HTMLDivElement | null>(null); // Reference for Menu Modal
  const menuMobileRef = useRef<HTMLDivElement | null>(null); // Reference for Menu Modal
  const confirmationRef = useRef<HTMLDivElement | null>(null); // Reference for Confirmation Modal
  const confirmationMobileRef = useRef<HTMLDivElement | null>(null); // Reference for Confirmation Modal
  const [_confirmationMenu, _setConfirmationMenu] = useState<any | null>(null);
  const [selectedApplications, setSelectedApplications] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false); // Track loading state
  const [isDownloading, setIsDownloading] = useState(false);
  const [dropdownValue, setDropdownValue] = useState<any>("SELECT_AN_OPTION");
  const [menuOpen, setMenuOpen] = useState<number | null>(null); // Track which menu is open
  const [applicationLength, setApplicationsLength] = useState(0);
  const [filteredApplications, setFilteredApplications] = useState(
    managerCourses[0]?.applications || []
  );
  const [applications, setApplications] = useState<any[]>(
    managerCourses[0]?.applications || []
  ); // State to hold applications
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate(); // Initialize navigate

  useEffect(() => {
    if (managerCourses[0]?.applications) {
      setFilteredApplications(managerCourses[0]?.applications);
      setApplications(managerCourses[0]?.applications || []);
    }
  }, [managerCourses]);

  useEffect(() => {
    if (selectedApplications.length > 0) {
      setDropdownEnabled(true); // Enable dropdown when at least one item is selected
    } else {
      setDropdownEnabled(false);
    }
  }, [selectedApplications]);

  // Close modals when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (menuRef.current && menuRef.current.contains(event.target as Node)) ||
        (menuMobileRef.current &&
          menuMobileRef.current.contains(event.target as Node)) ||
        (confirmationRef.current &&
          confirmationRef.current.contains(event.target as Node)) ||
        (confirmationMobileRef.current &&
          confirmationMobileRef.current.contains(event.target as Node))
      ) {
        return; // Don't close if clicking inside a modal or menu
      }

      // Close modals and menus if clicking outside
      setMenuOpen(null);
      setConfirmationMenu(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    dispatch(
      getManagerApplicationsApplyForTeam({
        status: "MANAGER_TEAM_APPLY_DETAILED",
        course_id: course_id,
      })
    );
  }, [dispatch]);

  const handleConfirmation = async (confirm: boolean) => {
    if (confirm && confirmationMenu) {
      const { action, isAllow, item } = confirmationMenu;
      const userId = sessionStorage.getItem("userId") || "";
      const dataToSend: any = {
        courseId: course_id,
        userId: item.user_id,
        manager_id: userId,
      };
      // Example: Call Redux API based on action
      if (action === "APPROVED_BY_MANAGER" && isAllow === true) {
        dataToSend.status = "APPROVED_BY_MANAGER";
        dataToSend.travel = true;
      } else if (action === "APPROVED_BY_MANAGER" && isAllow === false) {
        dataToSend.status = "APPROVED_BY_MANAGER";
        dataToSend.travel = false;
      }

      setConfirmationMenu(null);
      await dispatch(updateManagerApplicationStatus(dataToSend));

      if (applicationLength === 1) {
        navigate("/manager/apply-for-team");
        return;
      }

      // Step 2: Re-fetch the updated list of manager applications
      const response = await dispatch(
        getManagerApplicationsApplyForTeam({ status: "MANAGER_TEAM_APPLY" })
      );

      // Step 3: Update the selected course application after fetching
      if (response.payload) {
        const updatedCourse = response.payload.data.find(
          (course: any) => course.course_id === course_id
        );

        if (updatedCourse) {
          dispatch(setSelectedCourseApplication(updatedCourse));
        }
      }
    } else {
      setConfirmationMenu(null);
    }
  };

  const _handleOptionSelect = (action: string) => {
    if (action !== "SELECT_AN_OPTION") {
      _setConfirmationMenu({ action, items: selectedApplications }); // Show confirmation modal with selected items
      setDropdownValue(action);
    }
  };

  const handleOptionSelect = (action: string, isAllow: boolean, item: any) => {
    setMenuOpen(null); // Close menu
    setTimeout(() => {
      setConfirmationMenu({ action, isAllow, item }); // Open confirmation menu with a slight delay
    }, 50);
    // setConfirmationMenu({ action, isAllow, item }); // Open confirmation menu
  };

  const handleMenuClick = (index: number) => {
    setMenuOpen(menuOpen === index ? null : index); // Toggle menu open/close
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    application: any
  ) => {
    if (e.target.checked) {
      setSelectedApplications((prev) => [...prev, application]);
    } else {
      setSelectedApplications((prev) =>
        prev.filter((item) => item.user_id !== application.user_id)
      );
    }
  };

  const handleDownloadDescriptionFile = async () => {
    setIsDownloading(true);
    try {
      const response = await dispatch(
        downloadDescriptionFile({ course_id: course_id })
      );
      if (response.payload && response.payload.demo_file) {
        const fileUrl = response.payload.demo_file.startsWith('//') 
          ? `https:${response.payload.demo_file}` 
          : response.payload.demo_file;
        window.open(fileUrl, "_blank");
      } else {
        showToast(t("No download material for this course"), "info");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const _handleConfirmation = async (confirm: boolean) => {
    if (confirm && _confirmationMenu) {
      const { action, items } = _confirmationMenu;
      setIsLoading(true);

      // Call API for each selected item
      const promises = items.map(async (item: any) => {
        const userId = sessionStorage.getItem("userId") || "";
        const dataToSend = {
          courseId: course_id,
          userId: item.user_id,
          manager_id: userId,
          status: "APPROVED_BY_MANAGER",
          travel: action === "APPROVED_BY_MANAGER" ? true : false,
        };
        await dispatch(updateManagerApplicationStatus(dataToSend));
      });

      // Wait for all API calls to complete
      await Promise.all(promises);

      if (selectedApplications.length === applications.length) {
        navigate("/manager/apply-for-team");
        return;
      }
      // Refetch applications after update
      // Step 2: Re-fetch the updated list of manager applications
      const response = await dispatch(
        getManagerApplicationsApplyForTeam({ status: "MANAGER_TEAM_APPLY" })
      );

      // Step 3: Update the selected course application after fetching
      if (response.payload) {
        const updatedCourse = response.payload.data.find(
          (course: any) => course.course_id === course_id
        );

        if (updatedCourse) {
          dispatch(setSelectedCourseApplication(updatedCourse));
        }
      }

      setIsLoading(false);
      _setConfirmationMenu(null);
      setSelectedApplications([]); // Clear selected applications
      setDropdownValue("SELECT_AN_OPTION");
    } else {
      _setConfirmationMenu(null); // Close confirmation modal
      setDropdownValue("SELECT_AN_OPTION");
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // setCurrentPage(1);
    const applicationData = applications.filter((user: any) => {
      return user.user_name.toLowerCase().includes(value.toLowerCase());
    });
    setFilteredApplications(applicationData);
  };
  const isMobileOrTablet = window.innerWidth < 1024; // Checking for mobile/tablet screens

  return (
    <div className="flex flex-col items-start gap-8">
      {isMobileOrTablet ? (
        <>
          {" "}
          <TabNavbar
            id=""
            tabs={MY_TEAM_TABS}
            selectedTabId={1}
            type="Apply for team"
          />
          <BackButton
            to="/manager/apply-for-team"
            state={{ from: "ManagerApplyCourseSessions" }}
          />
          <Header />
        </>
      ) : (
        <>
          <BackButton
            to="/manager/apply-for-team"
            state={{ from: "ManagerApplyCourseSessions" }}
          />
          <Header />
        </>
      )}

      {isMobileOrTablet && (
        <div className="flex items-center w-full gap-5">
          <button
            className="py-1.5 px-5 bg-white uppercase disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: "#961e38", border: "1px solid #961e38" }}
            onClick={handleDownloadDescriptionFile}
            disabled={isDownloading}
          >
            {isDownloading ? t("Downloading...") : t("Download Description")}
          </button>
        </div>
      )}
      <div className="flex flex-row w-full justify-between items-center group cursor-pointer">
        <div className="flex flex-col w-full">
          <p className="text-2xl font-semibold text-[#961e38]">{course_name}</p>
          <div className="flex flex-wrap items-center justify-start gap-5 pt-2">
            <p className="text-xs font-semibold flex items-center justify-start gap-2">
              <span className="text-[#b5b5b5]">{t("Type")}:</span>
              <span>{category_type ? category_type : t("N/A")}</span>
            </p>
            <p className="text-xs font-semibold flex items-center justify-start gap-2">
              <span className="text-[#b5b5b5]">{t("Lang")}:</span>
              <span>
                {training_languages_oc ? training_languages_oc : t("N/A")}
              </span>
            </p>
            {minimum_job_level && (
              <p className="text-xs font-semibold flex items-center justify-start gap-2">
                <span className="text-[#b5b5b5]">{t("Min Career Lvl")}:</span>
                <span>{extractCareerLevelNumber(minimum_job_level) || t("N/A")}</span>
              </p>
            )}
            {selectedCourseApplication?.classroom_training_cost && (
              <p className="text-xs font-semibold flex items-center justify-start gap-2">
                <span className="text-[#b5b5b5]">
                  {t("Classroom Training Cost")}:
                </span>
                <span>
                  {selectedCourseApplication?.classroom_training_cost}
                </span>
              </p>
            )}
            {selectedCourseApplication?.online_training_cost && (
              <p className="text-xs font-semibold flex items-center justify-start gap-2">
                <span className="text-[#b5b5b5]">
                  {t("Online Training Cost")}:
                </span>
                <span>{selectedCourseApplication?.online_training_cost}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="w-full">
        <SearchWithFilter
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          disabled={loading}
          isApplyForTeam={true}
        />
      </div>

      {/* Mobile/Tablet View */}
      <div className="block md:hidden relative w-full">
        <div className="w-full mb-[4rem]">
          {loading || isLoading || updateLoading ? (
            <div>
              <div className="flex items-center w-full justify-center mt-4 mb-4">
                <SpinnerLoader />
              </div>
              {isLoading && (
                <p className="w-full text-center mt-8">
                  {t("Please do not refresh this page!")}
                </p>
              )}
            </div>
          ) : filteredApplications.length > 0 ? (
            filteredApplications.map((application: any, i: number) => {
              const { application_status, user_id, first_name } = application;
              return (
                <div
                  key={user_id}
                  className="border border-[#c6c6c6] py-2 text-left bg-[#f3f3f3] mt-2"
                >
                  <div className="px-4 border-b border-[#c6c6c6] pb-2">
                    <div className="flex items-center justify-between w-full">
                      <h3 className="font-[600] pb-2">{t("User")}</h3>
                      <input
                        type="checkbox"
                        onChange={(e) => handleCheckboxChange(e, application)}
                        className="ml-2"
                        disabled={application_status !== "ELIGIBLE"}
                      />
                    </div>

                    <p className="text-lg">
                      {first_name} {application?.last_name || ""}
                    </p>
                  </div>
                  <div className="flex justify-between items-center px-4 pt-2">
                    <div>
                      <h3 className="font-[600]">{t("Status")}</h3>
                      <p className="text-lg">{t(STATUS_MAP[application_status]?.label || application_status)}</p>
                    </div>{" "}
                    {application_status === "ELIGIBLE" && (
                      <button
                        style={{ color: "#961e38 !important" }}
                        onClick={() => {
                          setMenuOpen(null); // Close menu
                          setConfirmationMenu(null);
                          handleMenuClick(i);
                          setApplicationsLength(applications.length);
                        }}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                        title={t("More options")}
                      >
                        <FaPen color="#961e38" size={20} />
                      </button>
                    )}
                  </div>

                  {/* Conditional Menu */}
                  {/* Conditional Menu */}
                  {menuOpen === i && (
                    <div
                      ref={menuMobileRef} // Attach the ref to the menu modal
                      className="absolute z-10 bg-white shadow-md border rounded"
                      style={{ right: "2rem" }}
                    >
                      <ul>
                        {
                          <li
                            onClick={() =>
                              handleOptionSelect(
                                "APPROVED_BY_MANAGER",
                                true,
                                application
                              )
                            }
                            className="flex justify-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          >
                            {t("Apply and Approve for Travel")}
                          </li>
                        }
                        {
                          <li
                            onClick={() =>
                              handleOptionSelect(
                                "APPROVED_BY_MANAGER",
                                false,
                                application
                              )
                            }
                            className="flex justify-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          >
                            {t("Apply and Approve for No Travel")}
                          </li>
                        }
                      </ul>
                    </div>
                  )}
                  {/* Confirmation Menu */}
                  {confirmationMenu &&
                    confirmationMenu.item === application && (
                      <div
                        ref={confirmationMobileRef}
                        className="absolute z-20 bg-white shadow-md border rounded"
                        style={{ right: "2rem" }}
                      >
                        <p className="px-4 py-2">{t("Are you sure?")}</p>
                        <div className="flex justify-around px-4 py-2">
                          <button
                            onClick={() => handleConfirmation(true)}
                            className="text-green-500 hover:underline"
                          >
                            {t("Yes")}
                          </button>

                          <button
                            onClick={() => handleConfirmation(false)}
                            className="text-red-500 hover:underline"
                          >
                            {t("No")}
                          </button>
                        </div>
                      </div>
                    )}
                  {/* Confirmation Menu */}
                  {_confirmationMenu && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                      <div className="bg-white p-5 rounded-md shadow-lg flex flex-col justify-between items-end w-[90%] sm:w-[500px] h-[auto] max-h-[80vh]">
                        <div className="flex flex-col items-start justify-start gap-5 w-full">
                          <h2 className="text-lg font-semibold text-red-600">
                            {t("Confirm Action")}
                          </h2>
                          <p className="mt-2 text-left">
                            {t(
                              `Are you sure want to ${
                                _confirmationMenu.action ===
                                  "APPROVED_BY_MANAGER" &&
                                _confirmationMenu.isAllow
                                  ? "approve for travel"
                                  : "approve for no travel"
                              } these ${
                                _confirmationMenu.items.length
                              } applications?`
                            )}
                          </p>
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                          <button
                            className="px-4 py-2 bg-blue-500 text-white rounded"
                            onClick={() => _handleConfirmation(true)}
                          >
                            {t("Yes")}
                          </button>
                          <button
                            className="px-4 py-2 bg-red-500 text-white rounded"
                            onClick={() => _handleConfirmation(false)}
                          >
                            {t("No")}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <p className="w-full text-center py-5">{t("No Data Available")}</p>
          )}
        </div>
        <div className="fixed bottom-0 left-0 w-full p-4 bg-white">
          <div className="flex justify-between items-center ml-2">
            <select
              className="p-2 border"
              disabled={!dropdownEnabled} // Disable dropdown if no items are selected
              onChange={(e) => _handleOptionSelect(e.target.value)}
              value={dropdownValue}
            >
              <option value="SELECT_AN_OPTION" disabled selected>
                {t("Select a Bulk Action")}
              </option>
              <option value="APPROVED_BY_MANAGER">
                {t("Apply and Approve for Travel")}
              </option>
              <option value="APPROVED_BY_MANAGER_NO_ALLOW">
                {t("Apply and Approve for No Travel")}
              </option>
            </select>
          </div>
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden md:block w-full h-full">
        <div className="flex w-full items-center justify-between mb-4">
          <div className="flex items-center w-full gap-5">
            <button
              className="py-1.5 px-5 bg-white uppercase disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: "#961e38", border: "1px solid #961e38" }}
              onClick={handleDownloadDescriptionFile}
              disabled={isDownloading}
            >
              {isDownloading ? t("Downloading...") : t("Download Description")}
            </button>
          </div>
          <select
            className="p-2 border"
            disabled={!dropdownEnabled} // Disable dropdown if no items are selected
            onChange={(e) => _handleOptionSelect(e.target.value)}
            value={dropdownValue}
          >
            <option value="SELECT_AN_OPTION" disabled selected>
              {t("Select a Bulk Action")}
            </option>
            <option value="APPROVED_BY_MANAGER">
              {t("Apply and Approve for Travel")}
            </option>
            <option value="APPROVED_BY_MANAGER_NO_ALLOW">
              {t("Apply and Approve for No Travel")}
            </option>
          </select>
        </div>
        <table className="w-full border-collapse">
          <thead className="bg-[#961e38] w-full text-white rounded-t-xl">
            <tr>
              <th className="py-2 px-4 text-center">{t("Select")}</th>
              <th className="py-2 px-4 text-center">{t("Users")}</th>
              <th className="py-2 px-4 text-center">{t("Status")}</th>
              <th className="py-2 px-4 text-center">{t("Action")}</th>
            </tr>
          </thead>
          <tbody>
            {loading || isLoading || updateLoading ? (
              <tr>
                <td colSpan={4} className="py-5 px-4 text-center mt-4 mb-4">
                  {/* Center horizontally */}
                  <SpinnerLoader />
                  {isLoading && (
                    <p className="w-full text-center mt-8">
                      {t("Please do not refresh this page!")}
                    </p>
                  )}
                </td>
              </tr>
            ) : filteredApplications.length > 0 ? (
              filteredApplications.map((application: any, i: number) => {
                const { application_status, user_id, first_name } = application;
                return (
                  <tr key={user_id}>
                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                      <input
                        type="checkbox"
                        onChange={(e) => handleCheckboxChange(e, application)}
                        disabled={application_status !== "ELIGIBLE"}
                      />
                    </td>

                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                      {first_name} {application?.last_name || ""}
                    </td>
                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                      <p>{t(STATUS_MAP[application_status]?.label || application_status)}</p>
                    </td>

                    <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                      {application_status === "ELIGIBLE" && (
                        <button
                          onClick={() => {
                            setMenuOpen(null); // Close menu
                            setConfirmationMenu(null);
                            handleMenuClick(i);
                            setApplicationsLength(applications.length);
                          }}
                          className="text-gray-500 hover:text-gray-700 focus:outline-none"
                          title={t("More options")}
                        >
                          <FaEllipsisH size={20} />
                        </button>
                      )}

                      {/* Conditional Menu */}
                      {menuOpen === i && (
                        <div
                          ref={menuRef} // Attach the ref to the menu modal
                          className="absolute z-10 bg-white shadow-md border rounded"
                          style={{ right: "18rem" }}
                        >
                          <ul>
                            {
                              <li
                                onClick={() =>
                                  handleOptionSelect(
                                    "APPROVED_BY_MANAGER",
                                    true,
                                    application
                                  )
                                }
                                className="flex justify-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              >
                                {t("Apply and Approve for Travel")}
                              </li>
                            }
                            {
                              <li
                                onClick={() =>
                                  handleOptionSelect(
                                    "APPROVED_BY_MANAGER",
                                    false,
                                    application
                                  )
                                }
                                className="flex justify-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              >
                                {t("Apply and Approve for No Travel")}
                              </li>
                            }
                          </ul>
                        </div>
                      )}
                      {/* Confirmation Menu */}
                      {confirmationMenu &&
                        confirmationMenu.item === application && (
                          <div
                            ref={confirmationRef}
                            className="absolute z-20 bg-white shadow-md border rounded"
                            style={{ right: "22rem" }}
                          >
                            <p className="px-4 py-2">{t("Are you sure?")}</p>
                            <div className="flex justify-around px-4 py-2">
                              <button
                                onClick={() => handleConfirmation(true)}
                                className="text-green-500 hover:underline"
                              >
                                {t("Yes")}
                              </button>

                              <button
                                onClick={() => handleConfirmation(false)}
                                className="text-red-500 hover:underline"
                              >
                                {t("No")}
                              </button>
                            </div>
                          </div>
                        )}

                      {/* Confirmation Menu */}
                      {_confirmationMenu && (
                        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                          <div className="bg-white p-5 rounded-md shadow-lg flex flex-col justify-between items-end w-[90%] sm:w-[500px] h-[auto] max-h-[80vh]">
                            <div className="flex flex-col items-start justify-start gap-5 w-full">
                              <h2 className="text-lg font-semibold text-red-600">
                                {t("Confirm Action")}
                              </h2>
                              <p className="mt-2 text-left">
                                {t(
                                  `Are you sure want to ${
                                    _confirmationMenu.action ===
                                      "APPROVED_BY_MANAGER" &&
                                    _confirmationMenu.isAllow
                                      ? "approve for travel"
                                      : "approve for no travel"
                                  } these ${
                                    _confirmationMenu.items.length
                                  } applications?`
                                )}
                              </p>
                            </div>
                            <div className="mt-4 flex justify-end gap-2">
                              <button
                                className="px-4 py-2 bg-blue-500 text-white rounded"
                                onClick={() => _handleConfirmation(true)}
                              >
                                {t("Yes")}
                              </button>
                              <button
                                className="px-4 py-2 bg-red-500 text-white rounded"
                                onClick={() => _handleConfirmation(false)}
                              >
                                {t("No")}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={2}>
                  <p className="w-full text-center py-5">
                    {t("No Data Available")}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ManagerApplyForTeamApplications;
